import { useQueryClient } from '@tanstack/react-query'
import { Form, Input, message, Modal, Select } from 'antd'
import { useState } from 'react'

import client from '@/api/client'
import { unwrapApiData } from '@/api/unwrapApiResult'
import { UserSearchSelect } from '@/components/common/UserSearchSelect'
import { useClusters } from '@/hooks/useClusters'
import { useVmConfigurations } from '@/hooks/useVmConfigurations'
import { formatVmConfig } from '@/pages/user-groups/components/priceUtils'
import { invalidateAdminCaches } from '@/utils/cacheInvalidation'

interface DeployVmModalProps {
  open: boolean
  onClose: () => void
}

export function DeployVmModal({ open, onClose }: DeployVmModalProps) {
  const queryClient = useQueryClient()
  const [form] = Form.useForm()
  const [deploying, setDeploying] = useState(false)
  const clusters = useClusters()

  const { configurations: configs } = useVmConfigurations()

  const handleDeploy = async () => {
    try {
      const values = await form.validateFields()
      setDeploying(true)
      const result = await client.POST('/v2/admin/vms', { body: values })
      unwrapApiData(result, 'Failed to deploy VM')
      void message.success('VM deployed')
      form.resetFields()
      onClose()
      invalidateAdminCaches(queryClient)
    } catch (error) {
      if (error instanceof Error) {
        console.error(error)
        void message.error(error.message)
      }
    } finally {
      setDeploying(false)
    }
  }

  return (
    <Modal
      title="Deploy VM"
      open={open}
      onOk={handleDeploy}
      okText="Deploy VM"
      onCancel={onClose}
      confirmLoading={deploying}
      destroyOnHidden
      width={600}
    >
      <Form form={form} layout="vertical" preserve={false}>
        <Form.Item name="userId" label="User" rules={[{ required: true }]}>
          <UserSearchSelect onChange={(v) => form.setFieldValue('userId', v)} />
        </Form.Item>
        <Form.Item name="name" label="VM Name" rules={[{ required: true }]}>
          <Input placeholder="VM name" />
        </Form.Item>
        <Form.Item
          name="clusterId"
          label="Cluster"
          rules={[{ required: true }]}
        >
          <Select placeholder="Select cluster">
            {clusters.map((c) => (
              <Select.Option key={c.id} value={c.id}>
                {c.name ?? c.id}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          name="configurationId"
          label="Configuration"
          rules={[{ required: true }]}
        >
          <Select placeholder="Select configuration">
            {configs.map((c) => (
              <Select.Option key={c.id} value={c.id}>
                {formatVmConfig(c)}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          name="bootDiskId"
          label="Boot Disk ID"
          rules={[{ required: true }]}
        >
          <Input placeholder="Boot disk ID" />
        </Form.Item>
        <Form.Item name="sshKeys" label="SSH Keys">
          <Select mode="tags" placeholder="Enter SSH key IDs" />
        </Form.Item>
      </Form>
    </Modal>
  )
}
